import { Summarize } from "@mui/icons-material"
import { Box, Divider, Stack, Typography, useTheme } from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement } from "react"

import AssessEVDCDemandForm from "./assessEVDCDeviceForm"
import AssessHPDemandForm from "./assessHPDeviceForm"
import HeaderBanner from "./headerBanner"
import VerticalKeyValueList, {
    KeyValueType,
} from "../../components/dataDisplay/verticalKeyValueList"
import StatusChip from "../../components/pills/status/statusChip"
import {
    AppCard,
    AppCardContent,
    AppCardHeader,
} from "../../components/surfaces/appCard"
import { Datasets } from "../../constants/enums"
import {
    IFullEVDCDevice,
    IFullHPDevice,
} from "../../services/middleware/hooks/deviceRegisters/useGetFullDevice"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

const TIPS_Q_AND_A = [
    {
        question: "How do I pass additional comments to the manufacturer?",
        answer: "You can do this via the comments feed on the single device detail page. To get to this page, press back",
    },
    {
        question:
            "How do I view supporting files uploaded by the manufacturer?",
        answer: "You can view all supporting files on the single device detail page. To get to this page, press back. Manufacturers are able to upload up to 10 files per device",
    },
    {
        question: "Who can see my assessment?",
        answer: "The manufacturer, ENA and DNOs. The manufacturer is notified via email of the outcome of the assessment",
    },
    {
        question: "Why are some of the fields already filled in?",
        answer: "If some of the fields are already filled in, this is because this device is an amendment to a device that has already been assessed. These fields are carried through from the previous assessment. Please check that they are still correct and edit if not. When a new version of a device is created, these fields are not carried through",
    },
]

interface IAssessDemandDeviceDisplayProps {
    data: IFullEVDCDevice | IFullHPDevice
    isLoading: boolean
    dataset: Datasets
    isValidating: boolean
    mutate: () => void
}

/** Component for AssessDemandDevice webpage. Displays device details and
 * form for assessing device.
 */
const AssessDemandDeviceDisplay = ({
    data,
    isLoading,
    dataset,
    isValidating,
    mutate,
}: IAssessDemandDeviceDisplayProps): ReactElement => {
    const theme = useTheme()
    const coreDetailsKwargs: KeyValueType = {}
    const technicalDetailsKwargs: KeyValueType = {}
    Object.keys(data.core).forEach((key) => {
        coreDetailsKwargs[key] = data.core[key as keyof typeof data.core]
    })
    Object.keys(data.technical).forEach((key) => {
        technicalDetailsKwargs[key] =
            data.technical[key as keyof typeof data.technical]
    })
    return (
        <Grid container spacing={GRID_SPACING}>
            <Grid xs={12}>
                <Stack spacing={GRID_SPACING}>
                    <HeaderBanner
                        title="Device Assessment"
                        subtitle={data.core.Make}
                        subSubtitle={`${data.header["Device Ref"]} • ${dataset}`}
                        Icon={<Summarize />}
                        isValidating={isValidating}
                    />
                </Stack>
            </Grid>
            <Grid lg={6} xs={12}>
                <Stack spacing={GRID_SPACING}>
                    <AppCard>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                            }}
                        >
                            <AppCardHeader
                                title="Device Details"
                                titleTypographyProps={{
                                    variant: "h5",
                                    fontWeight: "500",
                                }}
                                subheader="Details on the device provided by the manufacturer"
                                subheaderTypographyProps={{
                                    variant: "subtitle1",
                                }}
                            />
                            <div style={{ marginLeft: "auto" }}>
                                <Typography
                                    color={theme.palette.grey[600]}
                                    variant="subtitle1"
                                    textAlign="right"
                                >
                                    Current Status
                                </Typography>
                                <StatusChip status={data.header.Status} />
                            </div>
                        </div>
                        <AppCardContent>
                            <Typography fontWeight="800" fontSize="large">
                                Core Details Summary
                            </Typography>
                            <VerticalKeyValueList kwargs={coreDetailsKwargs} />
                            <Typography
                                fontWeight="800"
                                fontSize="large"
                                paddingTop="8px"
                            >
                                Technical Details Summary
                            </Typography>
                            <VerticalKeyValueList
                                kwargs={technicalDetailsKwargs}
                            />
                        </AppCardContent>
                    </AppCard>
                    <AppCard>
                        <AppCardHeader
                            title="Tips"
                            titleTypographyProps={{
                                variant: "h5",
                                fontWeight: "500",
                            }}
                            subheader="Some tips and guidance on the process"
                            subheaderTypographyProps={{
                                variant: "subtitle1",
                            }}
                        />

                        <AppCardContent>
                            <Stack
                                direction="column"
                                spacing={GRID_SPACING}
                                divider={<Divider />}
                            >
                                {TIPS_Q_AND_A.map((tip) => (
                                    <Box
                                        borderLeft={3}
                                        color={theme.palette.primary.light}
                                        paddingLeft={3}
                                    >
                                        <Typography
                                            variant="body1"
                                            fontWeight={600}
                                            color="text.primary"
                                        >
                                            {tip.question}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                        >
                                            {tip.answer}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </AppCardContent>
                    </AppCard>
                </Stack>
            </Grid>
            <Grid lg={6} xs={12}>
                <Stack spacing={GRID_SPACING}>
                    <AppCard>
                        <AppCardHeader
                            title="Your Assessment"
                            titleTypographyProps={{
                                variant: "h5",
                                fontWeight: "500",
                            }}
                            subheader="Use this form to complete your assessment of the device. Once submitted, the device will be updated with your assessment and the manufacturer will be notified"
                            subheaderTypographyProps={{ variant: "subtitle1" }}
                        />
                        {dataset === Datasets.EVDC && (
                            <AssessEVDCDemandForm
                                data={data as IFullEVDCDevice}
                                isLoading={isLoading}
                                mutate={mutate}
                            />
                        )}
                        {dataset === Datasets.HP && (
                            <AssessHPDemandForm
                                data={data as IFullHPDevice}
                                isLoading={isLoading}
                                mutate={mutate}
                            />
                        )}
                        {dataset === Datasets.EB && (
                            <AssessHPDemandForm
                                data={data as IFullHPDevice}
                                isLoading={isLoading}
                                mutate={mutate}
                                dataset={Datasets.EB}
                            />
                        )}
                        {dataset === Datasets.ACU && (
                            <AssessHPDemandForm
                                data={data as IFullHPDevice}
                                isLoading={isLoading}
                                mutate={mutate}
                                dataset={Datasets.ACU}
                            />
                        )}
                        <AppCardContent />
                    </AppCard>
                </Stack>
            </Grid>
        </Grid>
    )
}

export default AssessDemandDeviceDisplay
